/* eslint-disable jsx-a11y/click-events-have-key-events */
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Route, Switch } from 'react-router-dom';

import { Text, Spacer } from '@zillow/constellation';
import { Container, Notification } from '@zillow/zrs-comet';

import { RMWContext } from 'app/shared/providers/RMWContext';
import { RoutingSelectors, UserManagementSelectors } from 'app/shared/selectors';
import { USER_MANAGEMENT_PAGE } from 'app/shared/constants/RouteConstants';
import ActivityHistoryPage from 'app/shared/containers/ActivityHistoryPage';
import AuthCheckWrapper from 'app/shared/containers/AuthCheckWrapper';
import HeadWrapper from 'app/shared/components/HeadWrapper';
import PermissionCheck from 'app/shared/components/PermissionCheck';
import UnrecognizedUsersPage from 'app/shared/containers/UnrecognizedUsersPage';
import UserManagementActions from 'app/shared/actions/UserManagementActions';
import UsersPage from 'app/shared/containers/UsersPage';
import UserManagementNav from './UserManagementNav';

import './style.scss';

const DEFAULT_PAGE_TITLE = 'User management';

const propTypes = {
    companyUsers: PropTypes.object,
};

const defaultProps = {
    companyUsers: {},
};

const contextTypes = {
    router: PropTypes.object.isRequired,
    paths: PropTypes.object,
};

const UserManagementPage = ({
    companyUsers,
    dispatch,
    company,
    activeTab,
}) => {
    const { paths } = React.useContext(RMWContext);
    React.useEffect(() => {
        dispatch(UserManagementActions.getCompany());
        dispatch(UserManagementActions.getUserManagementProfiles());
        return () => dispatch(UserManagementActions.clearUserManagementProfiles());
    }, []);

    const getSectionUrl = (section) => {
        const baseDetailsUrl = paths.userManagementPage;
        return `${baseDetailsUrl}/${section}([?].*)?`;
    };

    const renderChildRoutes = () => {
        // child paths
        const childPathUsers = getSectionUrl(paths.userManagementUsers);
        const childPathPending = getSectionUrl(paths.userManagementPending);
        const childPathActivity = getSectionUrl(paths.userManagementActivity);
        const childPathUnrecognized = getSectionUrl(paths.userManagementUnrecognizedUsers);
        return (
            <Switch>
                <Route component={UsersPage} path={childPathUsers} />
                <Route component={UsersPage} path={childPathPending} />
                <Route component={ActivityHistoryPage} path={childPathActivity} />
                <Route component={UnrecognizedUsersPage} path={childPathUnrecognized} />
            </Switch>
        );
    };

    const renderContent = () => {
        const { notification } = companyUsers;
        return (
            <PermissionCheck route={USER_MANAGEMENT_PAGE}>
                <Container className="UserManagementPage-container">
                    <div className="UserManagementPage-card">
                        {notification && (
                            <Notification
                                onClose={() => dispatch(UserManagementActions.setUsersNotification(null))}
                                type="success"
                            >
                                {companyUsers.notification}
                            </Notification>
                        )}
                        <Spacer marginY="sm">
                            <Text fontType="h4">{DEFAULT_PAGE_TITLE}</Text>
                        </Spacer>
                        <UserManagementNav company={company} activeTab={activeTab} />
                        {renderChildRoutes()}
                    </div>
                </Container>
            </PermissionCheck>
        );
    };

    return (
        <AuthCheckWrapper requireStrongAuthentication={false}>
            <div className="UserManagementPage">
                <HeadWrapper title={DEFAULT_PAGE_TITLE} />
                {renderContent()}
            </div>
        </AuthCheckWrapper>
    );
};

const mapStateToProps = state => ({
    router: RoutingSelectors.makeRouting(state),
    companyUsers: UserManagementSelectors.makeCompanyUsers(state),
    activeTab: UserManagementSelectors.makeActiveTab(state),
    company: UserManagementSelectors.makeCompany(state),
});

UserManagementPage.contextTypes = contextTypes;
UserManagementPage.propTypes = propTypes;
UserManagementPage.defaultProps = defaultProps;

export { UserManagementPage };
export default connect(mapStateToProps)(UserManagementPage);
